import type { ConsultantRecommendationResponse } from "./consultant-recommendation.types";
import { normalizeRecommendMarkdown } from "./format";

const entityTypeLabel = (entityType: string): string => {
  switch (entityType) {
    case "journey":
      return "Journey";
    case "course":
      return "Course";
    case "lesson":
      return "Lesson";
    case "module":
      return "Module";
    case "event":
      return "Event";
    case "blogPost":
      return "Blog post";
    case "resource":
      return "Resource";
    default:
      return entityType;
  }
};

const recommendationReason = (signals: string[]): string =>
  signals.length > 0
    ? `Matched ${signals.slice(0, 3).join(", ")}.`
    : "Selected based on overall fit with the requested role and learning goal.";

export const isGenericRecommendFallbackAnswer = (chatAnswer: string): boolean => {
  const normalized = chatAnswer.trim();
  if (!normalized) {
    return false;
  }
  const lacksWhyItFitsHeader = !/\*\*Why it fits:\*\*/i.test(normalized);
  if (!lacksWhyItFitsHeader) {
    return false;
  }
  const hasGeneratedFor = normalized.includes("Generated for:");
  const hasMixedPath = normalized.includes("A mixed learning path combining");
  const hasInlineBlueprintReason =
    /\s—\s(?:Journey|Course|Lesson|Module|Event|Blog post|Resource):/i.test(normalized);
  return (hasGeneratedFor && hasMixedPath) || hasInlineBlueprintReason;
};

export const buildConsultantRecommendationFromBlueprint = (
  response: ConsultantRecommendationResponse,
): string => {
  const blueprint = response.journeyBlueprint;
  const stepLines = blueprint.steps.slice(0, 8).map((step, index) => {
    const meta = [
      step.durationMinutes ? `${step.durationMinutes} min` : undefined,
      entityTypeLabel(step.entityType),
    ]
      .filter((entry): entry is string => Boolean(entry))
      .join(" · ");
    return `${index + 1}. **${step.title}**${meta ? ` (${meta})` : ""}\n\n**Why it fits:**\n\n${step.why}`;
  });
  const supportingLines = response.ranked
    .filter((entry) => ["blogPost", "resource", "event"].includes(entry.candidate.entityType))
    .slice(0, 3)
    .map((entry) => {
      const why =
        blueprint.steps.find(
          (step) =>
            step.entityId === entry.candidate.id ||
            step.title.toLowerCase() === entry.candidate.title.toLowerCase(),
        )?.why ?? recommendationReason(entry.matchedSignals);
      return `- **${entry.candidate.title}** (${entityTypeLabel(entry.candidate.entityType)})\n\n  **Why it fits:** ${why}`;
    });

  return normalizeRecommendMarkdown(
    [
      `## ${blueprint.title}`,
      "",
      blueprint.summary,
      "",
      "### Recommended study plan",
      ...stepLines,
      supportingLines.length > 0 ? "" : undefined,
      supportingLines.length > 0 ? "### Supporting content" : undefined,
      ...supportingLines,
      "",
      blueprint.totalEstimatedMinutes > 0
        ? `Estimated total time: **${blueprint.totalEstimatedMinutes} minutes**.`
        : undefined,
      "Open the canvas to review the full ranked list, sources, and feedback options.",
    ]
      .filter((line): line is string => typeof line === "string")
      .join("\n"),
  );
};

export const mergeStreamedRecommendIntro = (
  streamed: string,
  response: ConsultantRecommendationResponse,
): string => {
  const intro = normalizeRecommendMarkdown(streamed.trim());
  if (/###\s+Recommended study plan/i.test(intro)) {
    return intro;
  }
  const structured = buildConsultantRecommendationFromBlueprint(response);
  const planStart = structured.search(/###\s+Recommended study plan/i);
  if (planStart === -1) {
    return intro;
  }
  return normalizeRecommendMarkdown(`${intro}\n\n${structured.slice(planStart).trim()}`);
};

export const formatConsultantRecommendationMessage = (
  response: ConsultantRecommendationResponse,
): string => {
  const chatAnswer = response.chatAnswer?.trim();
  if (chatAnswer && !isGenericRecommendFallbackAnswer(chatAnswer)) {
    return normalizeRecommendMarkdown(chatAnswer);
  }
  return buildConsultantRecommendationFromBlueprint(response);
};

export const resolveRecommendMessageContent = (
  streamed: string,
  response: ConsultantRecommendationResponse,
): string => {
  const streamedTrimmed = streamed.trim();
  const formatted = formatConsultantRecommendationMessage(response);

  if (
    response.recommendationEngine === "meilisearch-chat" &&
    streamedTrimmed.length > 0 &&
    isGenericRecommendFallbackAnswer(response.chatAnswer?.trim() ?? formatted)
  ) {
    return mergeStreamedRecommendIntro(streamedTrimmed, response);
  }

  return formatted;
};
